// Anchor — the one cross-BC standoff-anchor primitive.
//
// `Target = { blob: ContentHash, selectors: NonEmpty<Selector> }` is a
// single "where" substrate that stable citations, per-element
// diagnostics, grounding markers, annotations, and judged attestations
// all resolve onto — replacing the four ad-hoc encodings in use today
// (`.claude/analysis/ingest-and-annotation.md` §5). Aliased `Core.Anchor.*`
// (`D.Anchor.*` in older call sites).
//
// It lives in its OWN core module — not inside any one BC — the same way
// `EntityRef` sits in `entityRef/`, because annotations, KB citations,
// diagnostics, and attestations all consume it. It imports only
// `ContentHash` and `ElementId` from the resource BC and defines
// everything else itself. This reuses the W3C-borrowed taxonomy sketched
// in `docs/model/bcs/resource/ast.md:194-224` (`TextQuoteSelector`,
// structural/element locators), giving it the content-hash pin the
// doc's version-pinned `ElementLocator` lacks.
//
// Standoff discipline (`decisions.jsonl` 86ceaaeb / 6f32fe1a / 50e16e6a):
//   - `blob` is the content-hash of the ORIGINAL bytes — the truth
//     substrate, NOT a document version. An anchor pinned to old bytes
//     orphans gracefully when re-ingest changes them (OD3).
//   - `selectors` carries >= 1 selector for W3C robustness: a fast one
//     (position) plus a drift-proof one (quote). The NonEmpty invariant
//     lives in the type — `mkTarget` never guards at runtime.
//   - `resolveSelector` is PURE: it matches against an already-fetched
//     extract string. The extract FETCH is impure and stays in the
//     shell (the lazy blob resolver). Keep the two apart.
//
// `TemporalSelector` (video) and `SpatialSelector` (image) are additive
// members of the `Selector` sum. Unlike text/position selectors they are
// drift-proof BY CONSTRUCTION — they carry their own coordinates (a time
// window; a normalised rectangle) rather than an offset into an extract,
// so they need no `resolveSelector` arm and resolve at the render edge
// (the video player seeks to `start`; the image overlay paints `rect`).
// Adding them left `Target` and every existing consumer untouched.
export { foldText, isByteTarget, isEntityTarget, isOrphaned, isResolvedSpan, isSpatial, isTemporal, isVersionTarget, matchResolution, mkElementSegment, mkEntityTarget, mkHeadingSegment, mkPageSegment, mkSpatialSelector, mkStructuralSelector, mkTarget, mkTemporalSelector, mkTextPositionSelector, mkTextQuoteSelector, mkVersionTarget, mkWholeFileSelector, mkWholeFileTarget, resolveSelector, toCharOffset, toHeadingSlug, toNormRect, toPageNumber, toSeconds, };
import { List, Maybe, Result } from "@bilda/utils";
import { findFolded, foldText, } from "./quoteMatch.js";
const toCharOffset = (n) => Number.isInteger(n) && n >= 0
    ? Result.ok(n)
    : Result.err(`char offset must be a non-negative integer, got ${n}`);
const toPageNumber = (n) => Number.isInteger(n) && n >= 1
    ? Result.ok(n)
    : Result.err(`page number must be an integer >= 1, got ${n}`);
const headingSlugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const toHeadingSlug = (s) => headingSlugPattern.test(s)
    ? Result.ok(s)
    : Result.err(`heading slug must be lowercase alphanumerics joined by hyphens, got "${s}"`);
const toSeconds = (n) => Number.isFinite(n) && n >= 0
    ? Result.ok(n)
    : Result.err(`seconds must be a non-negative finite number, got ${n}`);
const toNormRect = (rect) => {
    const { x, y, w, h } = rect;
    const finite = Number.isFinite(x) &&
        Number.isFinite(y) &&
        Number.isFinite(w) &&
        Number.isFinite(h);
    if (!finite)
        return Result.err(`norm rect fields must be finite, got {x:${x}, y:${y}, w:${w}, h:${h}}`);
    if (x < 0 || y < 0 || w < 0 || h < 0)
        return Result.err(`norm rect fields must be >= 0, got {x:${x}, y:${y}, w:${w}, h:${h}}`);
    if (x + w > 1 || y + h > 1)
        return Result.err(`norm rect must stay within the unit square (x+w<=1, y+h<=1), got {x:${x}, y:${y}, w:${w}, h:${h}}`);
    return Result.ok({ x, y, w, h });
};
const mkElementSegment = (elementId) => ({
    type: "element_segment",
    elementId,
});
const mkPageSegment = (page) => ({
    type: "page_segment",
    page,
});
const mkHeadingSegment = (slug) => ({
    type: "heading_segment",
    slug,
});
const wholeFileSelector = { type: "whole_file_selector" };
const mkWholeFileSelector = () => wholeFileSelector;
// mkTextPositionSelector gates the ONE invariant a position span carries
// that the type cannot: start <= end. Both offsets are already
// CharOffsets (>= 0), so a raw negative span is unrepresentable here.
const mkTextPositionSelector = (start, end) => start <= end
    ? Result.ok({ type: "text_position_selector", start, end })
    : Result.err(`text position start (${start}) must be <= end (${end})`);
// mkTextQuoteSelector gates a non-empty `exact` — an empty quote matches
// everything and anchors nothing.
const mkTextQuoteSelector = (exact, prefix, suffix) => exact.length > 0
    ? Result.ok({ type: "text_quote_selector", exact, prefix, suffix })
    : Result.err("text quote `exact` must be non-empty");
// mkStructuralSelector is total — the NonEmpty invariant is in the type.
const mkStructuralSelector = (path) => ({ type: "structural_selector", path });
// mkTemporalSelector gates the ONE invariant a time window carries that
// the type cannot: start <= end. Both bounds are already Seconds (>= 0),
// so a negative window is unrepresentable here.
const mkTemporalSelector = (start, end) => start <= end
    ? Result.ok({ type: "temporal_selector", start, end })
    : Result.err(`temporal start (${start}) must be <= end (${end})`);
// mkSpatialSelector is total — every invariant a spatial region carries
// lives in NormRect (gated by `toNormRect` at the boundary).
const mkSpatialSelector = (rect) => ({
    type: "spatial_selector",
    rect,
});
const mkTarget = (blob, selectors) => ({ blob, selectors });
// The whole-file convenience — the common "the entire file" anchor.
const mkWholeFileTarget = (blob) => mkTarget(blob, List.nonEmpty(mkWholeFileSelector()));
const mkEntityTarget = (ref) => ({
    type: "entity_target",
    ref,
});
const mkVersionTarget = (source, selectors) => ({ type: "version_target", source, selectors });
// `EntityTarget` and `VersionTarget` carry a `type` discriminant; the
// byte `Target` has none, so `"type" in a` is a total structural test.
const isEntityTarget = (a) => "type" in a && a.type === "entity_target";
const isVersionTarget = (a) => "type" in a && a.type === "version_target";
const isByteTarget = (a) => !("type" in a);
const isResolvedSpan = (r) => r.type === "resolved_span";
const isOrphaned = (r) => r.type === "orphaned";
const matchResolution = (cases) => (r) => r.type === "resolved_span" ? cases.resolved(r) : cases.orphaned(r);
const resolvedSpan = (start, end, text) => ({ type: "resolved_span", start, end, text });
const orphaned = (reason) => ({
    type: "orphaned",
    reason,
});
const isWholeFile = (s) => s.type === "whole_file_selector";
const isTextPosition = (s) => s.type === "text_position_selector";
const isTextQuote = (s) => s.type === "text_quote_selector";
// Temporal/spatial selectors resolve at the render edge, not here — but
// the guards live beside the others so consumers (the video player, the
// image overlay) narrow the sum the same way `resolveSelector` does.
const isTemporal = (s) => s.type === "temporal_selector";
const isSpatial = (s) => s.type === "spatial_selector";
// findQuote — pure, deterministic re-find of a quote in the extract via
// the shared tolerant matcher (`findFolded`: OCR/PDF drift such as
// whitespace runs, line-break hyphenation, ligatures, typographic marks),
// so the span carries the EXTRACT's own words, not the quote's variant.
// Verbatim occurrences win over folded-only ones; among those, prefer
// occurrences whose surrounding text matches BOTH given contexts, then
// prefix-only, then suffix-only, then the first. A structural selector is
// NOT resolved here — resolving a path needs the element tree, which this
// pure extract-only function does not have (that arrives in the tree-aware
// step); a lone structural selector falls through to whole-file/orphan.
const findQuote = (folded, q) => {
    const extract = folded.text;
    const found = findFolded(folded, q.exact);
    const verbatim = found.filter((s) => extract.slice(s.start, s.end) === q.exact);
    const spans = verbatim.length > 0 ? verbatim : found;
    const prefixOk = (s) => Maybe.match({
        just: (p) => extract.slice(Math.max(0, s.start - p.length), s.start) === p,
        nothing: () => true,
    })(q.prefix);
    const suffixOk = (s) => Maybe.match({
        just: (x) => extract.slice(s.end, s.end + x.length) === x,
        nothing: () => true,
    })(q.suffix);
    const chosen = spans.find((s) => prefixOk(s) && suffixOk(s)) ??
        spans.find(prefixOk) ??
        spans.find(suffixOk) ??
        spans[0];
    return chosen === undefined
        ? undefined
        : resolvedSpan(chosen.start, chosen.end, extract.slice(chosen.start, chosen.end));
};
// `extract` is folded once by the caller (`foldText`), so resolving many
// selectors against one large extract costs one fold, not one per quote.
const resolveSelector = (folded, selectors) => {
    const extract = folded.text;
    const all = List.toArray(selectors);
    const position = all.find(isTextPosition);
    const quote = all.find(isTextQuote);
    const wholeFile = all.find(isWholeFile);
    // Fast path: position, validated by quote if the target carries one.
    if (position !== undefined) {
        const inBounds = position.end <= extract.length;
        if (inBounds) {
            const text = extract.slice(position.start, position.end);
            if (quote === undefined)
                return resolvedSpan(position.start, position.end, text);
            // Quote is the tie-breaker: agree → position confirmed;
            // disagree (extract drifted) → the quote wins.
            if (text === quote.exact)
                return resolvedSpan(position.start, position.end, text);
            const refound = findQuote(folded, quote);
            if (refound !== undefined)
                return refound;
        }
        else if (quote !== undefined) {
            // Position drifted out of bounds → let the quote re-find it.
            const refound = findQuote(folded, quote);
            if (refound !== undefined)
                return refound;
        }
    }
    else if (quote !== undefined) {
        const refound = findQuote(folded, quote);
        if (refound !== undefined)
            return refound;
    }
    // Coarse fallback: whole-file spans the entire extract.
    if (wholeFile !== undefined)
        return resolvedSpan(0, extract.length, extract);
    // Nothing resolved — report the most specific reason attempted.
    if (quote !== undefined)
        return orphaned({ type: "quote_not_found" });
    if (position !== undefined)
        return orphaned({ type: "position_out_of_bounds" });
    return orphaned({ type: "no_resolvable_selector" });
};
//# sourceMappingURL=anchor.js.map